import React, { useEffect } from 'react';
import { useTranslationWithSelfFallback } from '@i18n/useTranslationWithSelfFallback';
import FormFieldContainer from '@components/ui/FormFieldContainer';
import InputField, { InputType } from '@components/ui/InputField';
import { isSafari, isMobile } from 'react-device-detect';

import Link from 'next/link';
import { useUI } from '@context/GlobalUIContext';
import styles from './LoginFields.module.scss';

interface LoginFormField {
  value: string;
  set: (value: string) => void;
  isValid: boolean;
}

interface LoginFormProps {
  email: LoginFormField;
  password: LoginFormField;
  isDirty: boolean;
}

/* tslint:disable:jsx-a11y/click-events-have-key-events jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */

const LoginFields = ({ email, password, isDirty }: LoginFormProps) => {
  const t = useTranslationWithSelfFallback('common');
  const { closeLoginModal } = useUI();

  // Prevent safari password filler scroll bug
  useEffect(() => {
    // We don't need to do this fix for other browsers. This issue is only on safari desktop
    if (!isSafari || isMobile) {
      return;
    }

    const loginElem = document.getElementById(
      'login-form-email'
    ) as HTMLFormElement;
    const passElem = document.getElementById(
      'login-form-password'
    ) as HTMLFormElement;

    if (!loginElem || !passElem) {
      return;
    }

    // Disable the login fields, then enable them again after a timeout. This prevents safari autofill from scrolling
    // the page down to the bottom.
    loginElem.disabled = true;
    passElem.disabled = true;

    const timeout = setTimeout(() => {
      loginElem.disabled = false;
      passElem.disabled = false;
    }, 250);

    return () => {
      clearTimeout(timeout);
      loginElem.disabled = false;
      passElem.disabled = false;
    };
  }, []);

  return (
    <>
      <FormFieldContainer
        label={t('login.email.label')}
        htmlFor="login-form-email"
        errorMessage={isDirty && !email.isValid && t('login.invalid.email')}
      >
        <InputField
          id="login-form-email"
          type={InputType.email}
          value={email.value}
          onChange={(value) => email.set(value)}
          hasError={isDirty && !email.isValid}
        />
      </FormFieldContainer>

      <FormFieldContainer
        label={t('login.password.label')}
        htmlFor="login-form-password"
        errorMessage={
          isDirty && !password.isValid && t('login.invalid.password')
        }
        footnote={
          <Link
            href={`/auth/forgot-password`}
            onClick={() => closeLoginModal()}
            className={styles.forgotPassword}
          >
            {t('forgot.password')}
          </Link>
        }
      >
        <InputField
          id="login-form-password"
          type={InputType.password}
          value={password.value}
          onChange={(value) => password.set(value)}
          hasError={isDirty && !password.isValid}
        />
      </FormFieldContainer>
    </>
  );
};

export default LoginFields;
